import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Check } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

// Hero Section
const ContactHero = () => (
  <section className="bg-[#1a2e44] py-20" data-testid="contact-hero">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <h1 className="text-4xl font-bold text-white mb-4 uppercase">
        Contact Us
      </h1>
      <p className="text-lg text-slate-300 max-w-2xl">
        Request a site assessment or get in touch with questions about our services.
      </p>
    </div>
  </section>
);

// Contact Form
const ContactForm = () => {
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const addressInputRef = useRef(null);
  
  // Attribution state
  const [attribution, setAttribution] = useState({
    landing_page: '',
    page_url: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
    referrer: '',
    neighborhood: '',
    lead_type: ''
  });
  
  const getInitialFormData = () => {
    const intent = searchParams.get('intent') || '';
    const service = searchParams.get('service') || '';
    const industry = searchParams.get('industry') || '';
    const source = searchParams.get('source') || '';
    return {
      name: '',
      email: '',
      phone: '',
      company: '',
      propertyType: industry,
      intent: intent,
      service: service,
      source: source,
      address: '',
      message: ''
    };
  };
  
  const [formData, setFormData] = useState(getInitialFormData);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places && addressInputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
        types: ['address'],
        componentRestrictions: { country: 'us' }
      });
      
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.formatted_address) {
          setFormData(prev => ({ ...prev, address: place.formatted_address }));
        }
      });
    }
  }, [isSubmitted]);

  // Capture UTM parameters and attribution data on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pathname = window.location.pathname;
    
    setAttribution({
      landing_page: pathname,
      page_url: window.location.href,
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_campaign: urlParams.get('utm_campaign') || '',
      utm_term: urlParams.get('utm_term') || '',
      utm_content: urlParams.get('utm_content') || '',
      referrer: document.referrer || '',
      neighborhood: '',
      lead_type: 'contact_form'
    });
  }, []);

  useEffect(() => {
    const intent = searchParams.get('intent');
    const service = searchParams.get('service');
    const industry = searchParams.get('industry');
    const source = searchParams.get('source');
    if (intent) setFormData(prev => ({ ...prev, intent }));
    if (service) setFormData(prev => ({ ...prev, service }));
    if (industry) setFormData(prev => ({ ...prev, propertyType: industry }));
    if (source) setFormData(prev => ({ ...prev, source }));
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

const API_URL = process.env.REACT_APP_BACKEND_URL;

  const getLocation = () => {
    return new Promise((resolve) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve(`${position.coords.latitude}, ${position.coords.longitude}`);
          },
          () => {
            resolve('Location not available');
          },
          { timeout: 5000 }
        );
      } else {
        resolve('Geolocation not supported');
      }
    });
  };

  const fireGAEvent = () => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'generate_lead', {
        utm_campaign: attribution.utm_campaign,
        landing_page: attribution.landing_page,
        neighborhood: attribution.neighborhood || 'website',
        lead_type: attribution.lead_type
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate phone format (at least 10 digits)
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      toast.error('Please enter a valid phone number.');
      setIsSubmitting(false);
      return;
    }

    // Validate SMS consent
    if (!smsConsent) {
      toast.error('You must agree to receive SMS messages to submit this form.');
      setIsSubmitting(false);
      return;
    }

    // Get location at submission time
    const locationData = await getLocation();

    try {
      // Build message with address if provided
      let fullMessage = formData.message;
      if (formData.address) {
        fullMessage = `Property Address: ${formData.address}\n\n${formData.message}`.trim();
      }
      
      const submitData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        property_type: formData.propertyType,
        intent: formData.intent,
        service: formData.service,
        source: formData.source,
        message: fullMessage,
        location: locationData,
        // Attribution fields
        landing_page: attribution.landing_page,
        page_url: attribution.page_url,
        utm_source: attribution.utm_source,
        utm_medium: attribution.utm_medium,
        utm_campaign: attribution.utm_campaign,
        utm_term: attribution.utm_term,
        utm_content: attribution.utm_content,
        referrer: attribution.referrer,
        neighborhood: attribution.neighborhood,
        lead_type: attribution.lead_type,
        sms_consent: smsConsent,
        consent_timestamp: smsConsent ? new Date().toISOString() : ''
      };

      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      // Fire GA4 generate_lead event
      fireGAEvent();

      setIsSubmitted(true);
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        propertyType: '',
        intent: '',
        service: '',
        source: '',
        address: '',
        message: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success State - Full page like landing pages
  if (isSubmitted) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="max-w-md w-full text-center">
          <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-200">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#1a2e44] mb-4">
              Thank You!
            </h2>
            <p className="text-slate-600 mb-6">
              We've received your request and will contact you as soon as possible to discuss your property needs.
            </p>
            <a 
              href="/"
              className="inline-block bg-[#1a2e44] hover:bg-[#0f1d2d] text-white font-medium py-3 px-6 rounded transition-colors"
            >
              Return to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      {/* 1. Full Name (required) */}
      <div className="space-y-2">
        <Label htmlFor="name" className="text-sm font-medium text-slate-700">
          Full Name *
        </Label>
        <Input
          id="name"
          name="name"
          type="text"
          value={formData.name}
          onChange={handleChange}
          required
          className="border-slate-300 focus:border-[#0E2344] focus:ring-[#0E2344]"
          data-testid="input-name"
        />
      </div>

      {/* 2. Phone Number (required) */}
      <div className="space-y-2">
        <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
          Phone Number *
        </Label>
        <Input
          id="phone"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleChange}
          required
          className="border-slate-300 focus:border-[#0E2344] focus:ring-[#0E2344]"
          data-testid="input-phone"
        />
      </div>

      {/* SMS Consent (required, directly below phone) */}
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          id="sms_consent"
          checked={smsConsent}
          onChange={(e) => setSmsConsent(e.target.checked)}
          required
          className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1a2e44] focus:ring-[#1a2e44]"
          data-testid="sms-consent-checkbox"
        />
        <label htmlFor="sms_consent" className="text-xs text-slate-600 leading-relaxed">
          By submitting this form, you agree to receive text messages from Sunlight Grounds Care regarding appointments, services, and account updates. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out or HELP for assistance.
        </label>
      </div>

      {/* 3. Email */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-slate-700">
            Email
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="border-slate-300 focus:border-[#0E2344] focus:ring-[#0E2344]"
            data-testid="input-email"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="company" className="text-sm font-medium text-slate-700">
            Company / Property Name
          </Label>
          <Input
            id="company"
            name="company"
            type="text"
            value={formData.company}
            onChange={handleChange}
            className="border-slate-300 focus:border-[#0E2344] focus:ring-[#0E2344]"
            data-testid="input-company"
          />
        </div>
      </div>

      {/* 4. Service Details */}
      <div className="space-y-2">
        <Label htmlFor="address" className="text-sm font-medium text-slate-700">
          Property Address
        </Label>
        <input
          ref={addressInputRef}
          id="address"
          name="address"
          type="text"
          value={formData.address}
          onChange={handleChange}
          placeholder="Start typing your address..."
          className="flex h-10 w-full rounded-md border border-slate-300 bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E2344] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="input-address"
          autoComplete="off"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-700">
            Property Type
          </Label>
          <Select 
            value={formData.propertyType} 
            onValueChange={(value) => handleSelectChange('propertyType', value)}
          >
            <SelectTrigger className="border-slate-300" data-testid="select-property-type">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="residential">Private Residence</SelectItem>
              <SelectItem value="hoa">HOA / Multi-Family</SelectItem>
              <SelectItem value="corporate">Corporate Campus</SelectItem>
              <SelectItem value="retail">Retail Center</SelectItem>
              <SelectItem value="hospitality">Hotel / Resort</SelectItem>
              <SelectItem value="education">Educational Facility</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-700">
            Inquiry Type *
          </Label>
          <Select 
            value={formData.intent} 
            onValueChange={(value) => handleSelectChange('intent', value)}
            required
          >
            <SelectTrigger className="border-slate-300" data-testid="select-intent">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="assessment">Site Assessment Request</SelectItem>
              <SelectItem value="quote">Service Quote</SelectItem>
              <SelectItem value="general">General Inquiry</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="text-sm font-medium text-slate-700">
          Message
        </Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={5}
          placeholder="Please describe your property and requirements..."
          className="border-slate-300 focus:border-[#0E2344] focus:ring-[#0E2344] resize-none"
          data-testid="input-message"
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-[#1a2e44] hover:bg-[#0f1d2d] text-white text-sm font-bold py-4 px-8 uppercase tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="submit-btn"
      >
        {isSubmitting ? 'Submitting...' : 'Submit Request'}
      </button>

      {/* Below submit - compliance text */}
      <p className="text-center text-slate-400 text-xs">
        Msg & data rates may apply.
      </p>

      {/* Privacy Policy / Terms links near form */}
      <div className="flex items-center justify-center gap-3">
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#1a2e44] text-xs underline hover:text-[#0f1d2d]" data-testid="form-privacy-link">
          Privacy Policy
        </a>
        <span className="text-slate-300 text-xs">|</span>
        <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#1a2e44] text-xs underline hover:text-[#0f1d2d]" data-testid="form-terms-link">
          Terms & Conditions
        </a>
      </div>
    </form>
  );
};

// Contact Info
const ContactInfo = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-xl font-bold text-[#1a2e44] mb-4">
        Contact Information
      </h2>
      <p className="text-slate-600 text-sm">
        We respond to all inquiries within one business day.
      </p>
    </div>

    <div className="space-y-4">
      <div className="flex items-start gap-4">
        <Phone className="w-5 h-5 text-[#1a2e44] mt-0.5" strokeWidth={1.5} />
        <div>
          <span className="block text-xs font-medium text-slate-500 mb-1">Phone</span>
          <a href="tel:6267372797" className="text-[#1a2e44] font-bold hover:text-[#1a2e44]/70">
            (626) 737-2797
          </a>
        </div>
      </div>

      <div className="flex items-start gap-4">
        <Mail className="w-5 h-5 text-[#1a2e44] mt-0.5" strokeWidth={1.5} />
        <div>
          <span className="block text-xs font-medium text-slate-500 mb-1">Email</span>
          <a href="mailto:info@sunlightgroundscare.com" className="text-[#1a2e44] font-bold hover:text-[#1a2e44]/70">
            info@sunlightgroundscare.com
          </a>
        </div>
      </div>

      <div className="flex items-start gap-4">
        <MapPin className="w-5 h-5 text-[#1a2e44] mt-0.5" strokeWidth={1.5} />
        <div>
          <span className="block text-xs font-medium text-slate-500 mb-1">Service Area</span>
          <span className="text-slate-700">Los Angeles, CA</span>
        </div>
      </div>
    </div>
  </div>
);

// Main Contact Section
const ContactSection = () => (
  <section className="bg-slate-50 py-16" data-testid="contact-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
        <div className="lg:col-span-2 bg-white p-8 border border-slate-200">
          <ContactForm />
        </div>
        <div>
          <ContactInfo />
        </div>
      </div>
    </div>
  </section>
);

export default function ContactPage() {
  useEffect(() => {
    document.title = 'Contact Us | Sunlight Grounds Care';
  }, []);

  return (
    <div data-testid="contact-page">
      <ContactHero />
      <ContactSection />
    </div>
  );
}
